import type { JSX, ReactNode } from 'react';

import { cn } from '../lib/cn';

export type AlertTone = 'info' | 'success' | 'warning' | 'danger';

const toneStyles: Record<AlertTone, string> = {
  info: 'border-slate-300 bg-slate-50 text-slate-800',
  success: 'border-emerald-300 bg-emerald-50 text-emerald-900',
  warning: 'border-amber-300 bg-amber-50 text-amber-900',
  danger: 'border-red-300 bg-red-50 text-red-900',
};

export interface AlertProps {
  tone?: AlertTone;
  title?: string;
  children: ReactNode;
  className?: string;
}

/**
 * A message the user has to notice.
 *
 * Warnings and failures are announced assertively; the rest politely. Reading
 * an "order confirmed" out loud over whatever somebody was doing is rude, and
 * staying silent about a failed delivery is worse than rude.
 */
export function Alert({ tone = 'info', title, children, className }: AlertProps): JSX.Element {
  const isUrgent = tone === 'warning' || tone === 'danger';

  return (
    <div
      role={isUrgent ? 'alert' : 'status'}
      aria-live={isUrgent ? 'assertive' : 'polite'}
      className={cn('rounded-md border px-4 py-3 text-sm', toneStyles[tone], className)}
    >
      {title === undefined ? null : <p className="mb-1 font-semibold">{title}</p>}
      {children}
    </div>
  );
}
